/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from "react";

import type { Locale } from "../../../shared/i18n";
import {
  Checkbox,
  DatePicker,
  DateTimePicker,
  Icons,
  Input,
  MultiSelect,
  ReferenceSelect,
  Select,
  Switch,
  Textarea,
  TimePicker,
} from "../../../shared/ui";
import {
  labelOfOption,
  optionsOf,
  unitOf,
  type FieldControl,
  type FieldRenderProps,
} from "./contracts";
import {
  asStringList,
  asText,
  formatDate,
  formatDateTime,
  formatTime,
  toDateTimeLocalValue,
} from "./format";

export type FieldRenderer = (
  props: FieldRenderProps & { locale: Locale },
) => ReactNode;

/** Plain text — user content is never interpreted as markup. */
function Display({ children }: { children: ReactNode }) {
  return <span className="text-body text-fg">{children}</span>;
}

function Print({ children }: { children: ReactNode }) {
  return <span>{children}</span>;
}

const textInput: FieldRenderer = ({ value, mode, onChange, id, ...aria }) => {
  if (mode === "print") return <Print>{asText(value)}</Print>;
  if (mode === "display") return <Display>{asText(value)}</Display>;
  return (
    <Input
      id={id}
      value={asText(value)}
      onChange={(event) => {
        onChange?.(event.target.value);
      }}
      {...aria}
    />
  );
};

const textarea: FieldRenderer = ({ value, mode, onChange, id, ...aria }) => {
  if (mode === "print") return <Print>{asText(value)}</Print>;
  if (mode === "display") return <Display>{asText(value)}</Display>;
  return (
    <Textarea
      id={id}
      value={asText(value)}
      onChange={(event) => {
        onChange?.(event.target.value);
      }}
      {...aria}
    />
  );
};

const numberInput: FieldRenderer = ({
  field,
  value,
  mode,
  onChange,
  id,
  ...aria
}) => {
  const unit = unitOf(field);
  const withUnit = unit ? `${asText(value)} ${unit}`.trim() : asText(value);
  if (mode === "print") return <Print>{withUnit}</Print>;
  if (mode === "display") return <Display>{withUnit}</Display>;
  return (
    <Input
      id={id}
      type="number"
      value={asText(value)}
      onChange={(event) => {
        onChange?.(
          event.target.value === "" ? null : Number(event.target.value),
        );
      }}
      {...aria}
    />
  );
};

const checkbox: FieldRenderer = ({ field, value, mode, onChange, id }) => {
  const checked = value === true;
  if (mode === "print") return <Print>{checked ? "[X]" : "[ ]"}</Print>;
  if (mode === "display")
    return (
      <Display>
        <span className="inline-flex items-center gap-1">
          {checked && <Icons.Check size={12} />}
          {field.label}
        </span>
      </Display>
    );
  return (
    <Checkbox
      id={id}
      label={field.label}
      checked={checked}
      onCheckedChange={onChange}
    />
  );
};

const switchControl: FieldRenderer = ({
  field,
  value,
  mode,
  onChange,
  id,
  labels,
}) => {
  const checked = value === true;
  if (mode === "print") return <Print>{checked ? "+" : "−"}</Print>;
  if (mode === "display")
    return <Display>{checked ? field.label : labels.empty}</Display>;
  return (
    <Switch
      id={id}
      label={field.label}
      checked={checked}
      onCheckedChange={onChange}
    />
  );
};

const datePicker: FieldRenderer = ({
  value,
  mode,
  onChange,
  locale,
  labels,
  id,
  ...aria
}) => {
  if (mode === "print")
    return <Print>{formatDate(value, locale, "print")}</Print>;
  if (mode === "display")
    return <Display>{formatDate(value, locale, "display")}</Display>;
  return (
    <DatePicker
      id={id}
      value={asText(value)}
      placeholder={labels.chooseDate}
      onValueChange={onChange}
      {...aria}
    />
  );
};

const timePicker: FieldRenderer = ({
  value,
  mode,
  onChange,
  labels,
  id,
  ...aria
}) => {
  if (mode === "print") return <Print>{formatTime(value)}</Print>;
  if (mode === "display") return <Display>{formatTime(value)}</Display>;
  return (
    <TimePicker
      id={id}
      value={formatTime(value)}
      placeholder={labels.chooseTime}
      onValueChange={onChange}
      {...aria}
    />
  );
};

const dateTimePicker: FieldRenderer = ({
  value,
  mode,
  onChange,
  locale,
  labels,
  id,
  ...aria
}) => {
  if (mode === "print")
    return <Print>{formatDateTime(value, locale, "print")}</Print>;
  if (mode === "display")
    return <Display>{formatDateTime(value, locale, "display")}</Display>;
  return (
    <DateTimePicker
      id={id}
      value={toDateTimeLocalValue(value)}
      placeholder={labels.chooseDate}
      onValueChange={onChange}
      {...aria}
    />
  );
};

const select: FieldRenderer = ({
  field,
  value,
  mode,
  onChange,
  labels,
  id,
  ...aria
}) => {
  const label = typeof value === "string" ? labelOfOption(field, value) : "";
  if (mode === "print") return <Print>{label}</Print>;
  if (mode === "display") return <Display>{label}</Display>;
  return (
    <Select
      id={id}
      value={typeof value === "string" ? value : undefined}
      options={optionsOf(field)}
      placeholder={labels.choose}
      onValueChange={onChange}
      {...aria}
    />
  );
};

const multiSelect: FieldRenderer = ({
  field,
  value,
  mode,
  onChange,
  labels,
  locale,
  id,
}) => {
  const values = asStringList(value);
  const joined = new Intl.ListFormat(locale, {
    style: "long",
    type: "conjunction",
  }).format(values.map((entry) => labelOfOption(field, entry)));
  if (mode === "print") return <Print>{joined}</Print>;
  if (mode === "display") return <Display>{joined}</Display>;
  return (
    <MultiSelect
      id={id}
      values={values}
      options={optionsOf(field)}
      placeholder={labels.choose}
      removeLabel={labels.remove}
      onValuesChange={onChange}
    />
  );
};

const referenceSelect: FieldRenderer = ({
  field,
  value,
  mode,
  onChange,
  labels,
  id,
  ...aria
}) => {
  const id_ = typeof value === "string" ? value : "";
  const label = id_ ? labelOfOption(field, id_) : "";
  // On paper the identifier is spelled out; on screen it stays hidden behind
  // the human-readable name.
  if (mode === "print")
    return <Print>{label && id_ ? `${label} (#${id_})` : label}</Print>;
  if (mode === "display")
    return (
      <Display>
        <span className="inline-flex items-center gap-2">
          <span aria-hidden="true" className="size-2 rounded-full bg-accent" />
          {label}
        </span>
      </Display>
    );
  return (
    <ReferenceSelect
      id={id}
      value={id_ || undefined}
      options={optionsOf(field)}
      placeholder={labels.choose}
      onValueChange={onChange}
      {...aria}
    />
  );
};

/**
 * Ten controls × three modes. Mirrors WIDGET_RENDERERS in ../registry.tsx: a
 * `satisfies Record<…>` so a new control in the compiled manifest cannot land
 * without a renderer, and an unknown one is refused rather than guessed.
 */
export const FIELD_RENDERERS = {
  text_input: textInput,
  textarea,
  number_input: numberInput,
  checkbox,
  switch: switchControl,
  date_picker: datePicker,
  time_picker: timePicker,
  datetime_picker: dateTimePicker,
  select,
  multi_select: multiSelect,
  reference_select: referenceSelect,
} satisfies Record<FieldControl, FieldRenderer>;
